import React, { useState } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../firebase";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../../design/dialog";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
} from "../../../design/fieldset";
import { Input } from "../../../design/input";
import { Textarea } from "../../../design/textarea";
import { useVisit } from "../../../contexts/VisitContext";
import { useAuth } from "../../../contexts/AuthContext";
import { parseDateToTimestamp, todayLocalDateString } from "../../../utils/date";
import { Visit, VisitPaymentStatus, storagePaths } from "@visitproof/types";
import { useAccount } from "../../../contexts/AccountContext";
import {
  BanknotesIcon,
  CheckCircleIcon,
  DocumentArrowUpIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

interface MarkAsPaidButtonProps {
  visit: Visit;
}

const MarkAsPaidButton: React.FC<MarkAsPaidButtonProps> = ({ visit }) => {
  const { updateVisit } = useVisit();
  const { user } = useAuth();
  const { account } = useAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState<string>(
    todayLocalDateString()
  );
  const [paymentNotes, setPaymentNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPaid = visit.paymentStatus === "paid";

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let paymentFileUrl: string | undefined;
      let paymentFileName: string | undefined;

      // Upload file if provided
      if (file) {
        const fileName = `${Date.now()}_${file.name}`;
        const path = account?.id
          ? storagePaths.visitPayment(account.id, visit.id, fileName)
          : `visits/${visit.id}/payment/${fileName}`;
        const fileRef = ref(storage, path);
        await uploadBytes(fileRef, file);
        paymentFileUrl = await getDownloadURL(fileRef);
        paymentFileName = file.name;
      }

      // Update visit with payment info
      await updateVisit(visit.id, {
        paymentStatus: "paid" as VisitPaymentStatus,
        paymentDate: parseDateToTimestamp(paymentDate),
        paymentNotes: paymentNotes || undefined,
        paymentFileUrl,
        paymentFileName,
        paymentMarkedBy: user?.uid,
      });

      clearForm();
    } catch (error) {
      console.error("Error marking visit as paid:", error);
      alert("Failed to mark visit as paid. Please try again.");
    }

    setIsSubmitting(false);
  };

  const handleMarkAsUnpaid = async () => {
    setIsSubmitting(true);

    try {
      await updateVisit(visit.id, {
        paymentStatus: "unpaid" as VisitPaymentStatus,
        paymentDate: undefined,
        paymentNotes: undefined,
        paymentFileUrl: undefined,
        paymentFileName: undefined,
        paymentMarkedBy: undefined,
      });
    } catch (error) {
      console.error("Error marking visit as unpaid:", error);
      alert("Failed to mark visit as unpaid. Please try again.");
    }

    setIsSubmitting(false);
  };

  const clearForm = () => {
    setPaymentDate(todayLocalDateString());
    setPaymentNotes("");
    setFile(null);
    setIsOpen(false);
  };

  if (isPaid) {
    return (
      <Button
        outline
        onClick={handleMarkAsUnpaid}
        disabled={isSubmitting}
        className="flex items-center gap-2"
      >
        <XCircleIcon className="h-4 w-4" />
        {isSubmitting ? "Updating..." : "Mark as Unpaid"}
      </Button>
    );
  }

  return (
    <>
      <Button
        color="green"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2"
      >
        <BanknotesIcon className="h-4 w-4" />
        Mark as Paid
      </Button>

      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Mark Visit as Paid</DialogTitle>
        <DialogDescription>
          Record the payment details for this visit. You can optionally upload a
          receipt or payment confirmation.
        </DialogDescription>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </Field>

              <Field>
                <Label>Payment Notes (optional)</Label>
                <Textarea
                  value={paymentNotes}
                  placeholder="e.g., Check #1234, Venmo transaction, etc."
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  rows={3}
                />
              </Field>

              <Field>
                <Label>Payment Evidence (optional)</Label>
                <div className="mt-1">
                  {file ? (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
                      <DocumentArrowUpIcon className="h-5 w-5 text-zinc-500" />
                      <span className="text-sm text-zinc-700 dark:text-zinc-300 flex-1 truncate">
                        {file.name}
                      </span>
                      <Button plain onClick={() => setFile(null)}>
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <Input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) =>
                        setFile(e.target.files ? e.target.files[0] : null)
                      }
                    />
                  )}
                  <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                    Upload a receipt, screenshot, or payment confirmation (PDF or
                    image)
                  </p>
                </div>
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button
            color="green"
            disabled={!paymentDate || isSubmitting}
            onClick={handleSubmit}
            className="flex items-center gap-2"
          >
            <CheckCircleIcon className="h-4 w-4" />
            {isSubmitting ? "Saving..." : "Confirm Payment"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MarkAsPaidButton;
